/**
 * `/room/:id/catchup` — the personal catch-up. Joins the room (the director only narrates rooms
 * you're in), asks the director once per language, plays the story, then lands on your verdict.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { DirectorLanguage, Fan } from '@clutch/core';
import { useClient } from '../client';
import { navigate } from '../router';
import { teamAccent } from '../ui/color';
import { DirectorBusyError, DirectorUnavailableError, requireModelShow } from '../director/api';
import { DirectorUnavailable } from '../director/Unavailable';
import { GENERATION_STAGES, STAGE_COPY, stageStatuses, type GenerationStage } from '../director/generation';
import { DebugInspector, debugEnabled } from '../director/DebugInspector';
import { StoryPlayer } from '../director/StoryPlayer';
import { VerdictCard } from '../director/VerdictCard';
import { PublishSheet } from '../director/PublishSheet';
import { verdictFor } from '../director/perspective';
import { strings } from '../director/strings';
import { LangToggle } from '../director/LangToggle';
import type { Lens } from '../director/moment-scene';
import { captureCatchUp, CatchUpSession, type BoundCatchUp } from '../director/catchup-session';
import { htmlLang, pick } from '../director/i18n';
import { store } from '../client';
import { TeamLogo } from '../ui/Media';
import { providerCovers, SpeechCache } from '../director/voice';
import { DirectorSheet, sheetCopy, useStudio, type SheetGame } from '../director/DirectorSheet';
import { ENERGY_COPY, STYLE_COPY, draftFrom, profilePatch, requestOf, selectedVoice, showFromProfile, showOfDraft, takeKey, voiceGate, type Focus, type ShowDraft, type VoiceGate } from '../director/show';
import { directorTeamName, type DirectorAudio, type DirectorError, type DirectorProfile, type DirectorShow } from '@clutch/core';

type Load = { phase: 'standby' } | { phase: 'loading' } | { phase: 'busy' } | { phase: 'error'; message?: string } | { phase: 'unsupported' }
  | { phase: 'unavailable'; error: DirectorError; story?: BoundCatchUp }
  | { phase: 'ready'; story: BoundCatchUp; narrated: boolean };

export function CatchUp({ roomId, fan, initialLanguage }: { roomId: string; fan: Fan; initialLanguage?: DirectorLanguage }) {
  const client = useClient();
  const api = client.director;
  const [lang, setLang] = useState<DirectorLanguage | undefined>(initialLanguage);
  const [followed, setFollowed] = useState<string[]>(fan.team ? [fan.team] : []);
  const [load, setLoad] = useState<Load>(api ? { phase: 'loading' } : { phase: 'unsupported' });
  const [publishing, setPublishing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [direct] = useState(() => new URLSearchParams(location.search).get('direct') === '1');
  // `draft` is what the sheet is editing; `aired` is the take on screen (its voice, credit, lens).
  const [draft, setDraft] = useState<ShowDraft>(() => draftFrom({ fanTeam: fan.team, local: localShow(), localFocus: localFocus(), ...(initialLanguage ? { language: initialLanguage } : {}) }));
  const [aired, setAired] = useState<ShowDraft>(draft);
  const draftRef = useRef(draft);
  const [sheet, setSheet] = useState<'direct' | 'remix'>();
  const [game, setGame] = useState<SheetGame>();
  const [banner, setBanner] = useState<{ team: string; at: number }>();
  const [stage, setStage] = useState<GenerationStage>('research');
  const studio = useStudio();
  const session = useRef<CatchUpSession>();
  const request = useRef(0);
  const seenSent = useRef(new Set<string>());
  const index = useRef(0);

  /** Speech per take (director + voice): produced before chapter one, reused by the player. */
  const speechFor = useRef(new SpeechCache());
  const speechSegments = useRef(new Map<string, string[]>());
  const produceSpeech = useCallback((directorId: string, voiceId?: string): Promise<DirectorAudio> | undefined => {
    const key = `${directorId}|${voiceId ?? ''}`;
    return api ? speechFor.current.get(key, speechSegments.current.get(directorId) ?? [], () => api.speech(directorId, fan.fanId, voiceId)) : undefined;
  }, [api, fan.fanId]);

  const generate = useCallback(async (language: DirectorLanguage, current = session.current) => {
    if (!current) return;
    const revision = ++request.current;
    const voiceId = draftRef.current.voiceId;
    const live = () => session.current === current && request.current === revision;
    setLoad({ phase: 'loading' });
    setStage('compose');
    let story: BoundCatchUp | undefined;
    try {
      story = await current.select(language, takeKey(draftRef.current));
      if (!story || !live()) return;
      setStage('facts');
      requireModelShow(story.director);
    } catch (e) {
      if (!live()) return;
      if (e instanceof DirectorUnavailableError) return setLoad({ phase: 'unavailable', error: e.detail });
      return setLoad(e instanceof DirectorBusyError ? { phase: 'busy' } : { phase: 'error' });
    }
    // Audio is the master clock: chapter one starts only once its narration is produced and buffered.
    setStage('voice');
    try {
      speechSegments.current.set(story.director.directorId, story.director.segments.map((s) => s.segmentId));
      const audio = await produceSpeech(story.director.directorId, voiceId);
      if (!live()) return;
      const reason = audio?.reason;
      if (!providerCovers(audio, story.director.segments.map((s) => s.segmentId))) {
        return setLoad({ phase: 'unavailable', story, error: { code: 'AUDIO_UNAVAILABLE', message: reason ?? 'Narration unavailable', retriable: true, ...(reason ? { reasons: [reason] } : {}) } });
      }
      setStage('sync');
      const first = audio.segments.find((a) => a.segmentId === story!.director.segments[0]!.segmentId);
      if (first && api) await bufferAudio(api.media(first.url));
      if (live()) setLoad({ phase: 'ready', story, narrated: true });
    } catch (e) {
      if (!live()) return;
      setLoad({ phase: 'unavailable', story, error: e instanceof DirectorUnavailableError ? e.detail : { code: 'AUDIO_UNAVAILABLE', message: e instanceof Error ? e.message : 'Narration unavailable', retriable: true } });
    }
  }, [api, produceSpeech]);

  // Join before requesting evidence, including when ?lang is explicit. The first snapshot fixes
  // the cutoff for this visit; later language requests and cache hits reuse the whole same view.
  useEffect(() => {
    if (!api) return;
    let alive = true;
    session.current?.cancel();
    session.current = undefined;
    request.current++;
    index.current = 0;
    setLoad({ phase: 'loading' });
    (async () => {
      let language = initialLanguage;
      try {
        const profile = await api.profile(fan.fanId);
        if (alive) setFollowed(profile.followedTeams.length ? profile.followedTeams : fan.team ? [fan.team] : []);
        language ??= profile.language;
        if (alive) {
          const d = draftFrom({ profile, fanTeam: fan.team, local: localShow(), localFocus: localFocus(), language });
          draftRef.current = d;
          setDraft(d);
          setAired(d);
        }
      } catch {
        /* default below */
      }
      try {
        if (!alive) return;
        await client.joinRoom(roomId, fan.fanId);
        if (!alive) return;
        const snapshot = await client.getRoom(roomId);
        const plays = await client.plays(roomId);
        if (!alive) return;
        const evidence = captureCatchUp(snapshot, plays);
        if (evidence.roomId !== roomId) throw new Error('Wrong catch-up room');
        if (alive) setStage('compose');
        // The sheet's show settings ride along as a per-request override (request › profile).
        const current = new CatchUpSession(evidence, fan.fanId, async (selected, asOfSeq) => {
          const body = requestOf(draftRef.current);
          const show = body.show;
          // The flagship path is strict: a model-authored show or a typed error, never a template.
          return requireModelShow(await api.catchUp(roomId, { fanId: fan.fanId, asOfSeq, ...body, show, language: selected, require: 'model' }));
        });
        session.current = current;
        setGame({ away: snapshot.meta.away.tricode, home: snapshot.meta.home.tricode, players: draftRef.current.focus.kind === 'player' ? [draftRef.current.focus.name] : [] });
        setLang(language ?? 'en');
        if (direct) {
          setLoad({ phase: 'standby' });
          setSheet('direct');
        } else void generate(language ?? 'en', current);
      } catch {
        if (alive) setLoad({ phase: 'error' });
      }
    })();
    return () => { alive = false; session.current?.cancel(); session.current = undefined; request.current++; };
  }, [api, client, roomId, fan.fanId, fan.team, initialLanguage, attempt, generate, direct]);

  /** Remember the sheet on the profile; an older server without `show` still keeps the rest. */
  const remember = useCallback(async (d: ShowDraft) => {
    saveLocal(d);
    if (!api) return;
    const patch = profilePatch(d);
    let saved: DirectorProfile | undefined;
    try {
      saved = await api.saveProfile(fan.fanId, patch);
    } catch {
      const rest: Partial<DirectorProfile> = { ...patch };
      delete rest.show;
      saved = await api.saveProfile(fan.fanId, rest).catch(() => undefined);
    }
    if (saved) setFollowed(saved.followedTeams.length ? saved.followedTeams : []);
  }, [api, fan.fanId]);

  /** Go to air with a take: save it, then generate (instant when this take is cached). */
  const air = useCallback(async (d: ShowDraft, gate: VoiceGate, pov?: string) => {
    setSheet(undefined);
    draftRef.current = d;
    setDraft(d);
    setPublishing(false);
    setLoad({ phase: 'loading' });
    await remember(d);
    setAired(d);
    setLang(d.language);
    // Generate was an explicit tap: narration may start with it when the server can speak.
    if (pov) setBanner({ team: pov, at: Date.now() });
    index.current = 0;
    void generate(d.language);
  }, [remember, generate]);

  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(undefined), 2600);
    return () => clearTimeout(t);
  }, [banner]);

  const switchLang = (l: DirectorLanguage) => {
    if (l === lang) return;
    draftRef.current = { ...draftRef.current, language: l };
    setDraft(draftRef.current);
    setAired((a) => ({ ...a, language: l }));
    setLang(l);
    setPublishing(false);
    void generate(l);
    void api?.saveProfile(fan.fanId, { language: l }).catch(() => undefined);
  };
  const retry = () => session.current && lang ? void generate(lang) : setAttempt((n) => n + 1);

  const close = () => navigate(`/room/${encodeURIComponent(roomId)}?from=catchup`);
  const t = strings(lang ?? 'en');
  const gate = voiceGate(studio.caps === 'loading' ? 'loading' : studio.caps, studio.voices, aired.language);
  const cancelAir = () => {
    session.current?.cancel();
    request.current++;
    setLoad({ phase: 'standby' });
    setSheet(load.phase === 'loading' && aired ? 'remix' : 'direct');
  };
  const sheetEl = sheet && game ? (
    <DirectorSheet game={game} draft={draft} mode={sheet} onChange={(d) => { draftRef.current = d; setDraft(d); }}
      onGenerate={(d, g) => void air(d, g)}
      onClose={() => {
        setSheet(undefined);
        draftRef.current = aired;
        setDraft(aired);
      }} />
  ) : null;

  if (load.phase !== 'ready') {
    return (
      <main className="cu cu--state" lang={htmlLang(lang ?? 'en')} data-testid="catchup" data-phase={load.phase}>
        <button className="sp__close cu__x" onClick={close} aria-label={t.close}>✕</button>
        {session.current && lang && <LangToggle value={lang} onChange={switchLang} />}
        {load.phase === 'loading' && <GoingToAir draft={aired} gate={gate} game={game} stage={stage} loading={t.loading} {...(session.current ? { onCancel: cancelAir } : {})} />}
        {load.phase === 'unavailable' && !sheet && <DirectorUnavailable error={load.error} language={lang ?? 'en'} onRetry={retry} onBack={close}
          onVoice={() => setSheet('direct')}
          {...(load.story ? { onTranscript: () => setLoad({ phase: 'ready', story: load.story!, narrated: false }) } : {})}
          {...(load.error.code === 'UNSUPPORTED_LOCALE' ? { onLanguage: () => setSheet('direct') } : {})} />}
        {load.phase === 'standby' && game && !sheet && (
          <div className="cu__card cu__standby" role="status" data-testid="studio-standby">
            <span className="air__tally" aria-hidden="true" />
            <p>{sheetCopy(lang ?? 'en').skip}</p>
            <button className="btn btn--primary" onClick={() => void air(draftRef.current, gate)} data-testid="standby-go">{sheetCopy(lang ?? 'en').go}</button>
            <button className="btn btn--ghost" onClick={() => setSheet('direct')} data-testid="standby-direct">{sheetCopy(lang ?? 'en').direct}</button>
          </div>
        )}
        {sheetEl}
        {load.phase === 'busy' && <StateCard title={t.busy} action={t.retry} onAction={retry} />}
        {load.phase === 'error' && <StateCard title={t.failed} action={t.retry} onAction={retry} />}
        {load.phase === 'unsupported' && <StateCard title={t.serverRequired} action={t.backToRoom} onAction={close} />}
      </main>
    );
  }

  const { director: d, plays, live, friend } = load.story;
  const narrated = load.narrated;
  const inspect = (id: string) => api?.inspector?.(id, fan.fanId) ?? Promise.resolve(undefined);
  const l = d.language;
  if (d.segments.length === 0 || d.asOf.seq < 0) {
    return (
      <main className="cu cu--state" lang={htmlLang(l)} data-testid="catchup" data-phase="empty">
        <button className="sp__close cu__x" onClick={close} aria-label={t.close}>✕</button>
        <StateCard title={t.empty} action={t.backToRoom} onAction={close} />
      </main>
    );
  }
  const verdict = verdictFor({ director: d, language: l, followed, plays });
  const lens = lensFor(followed, d.familiarity);
  const final = d.asOf.status === 'final';
  const style = { '--a': teamAccent(d.game.away.tricode), '--h': teamAccent(d.game.home.tricode) } as CSSProperties;
  const voice = selectedVoice(gate, aired.voiceId);
  const effective: DirectorShow = showFromProfile({ show: (d as { show?: Partial<DirectorShow> }).show ?? showOfDraft(aired) });
  const directed = d.narration?.mode === 'model' || d.generation.mode === 'model';
  const pov = aired.focus.kind === 'team' ? aired.focus.team : undefined;
  const teams = [d.game.away.tricode, d.game.home.tricode];
  const sc = sheetCopy(l);
  const voiceName = voice?.name ?? (d as { show?: { voiceName?: string } }).show?.voiceName ?? (gate.state === 'house' ? sc.house : gate.state === 'not-connected' ? pick({ en: 'Captions', hi: 'कैप्शन', zh: '字幕' }, l) : pick({ en: 'AI voice', hi: 'AI आवाज़', zh: 'AI语音' }, l));

  return (
    <main className="cu" lang={htmlLang(l)} style={style} data-testid="catchup" data-phase="ready">
      <StoryPlayer
        key={`${d.directorId}|${aired.voiceId ?? ''}`}
        director={d}
        plays={plays}
        live={live}
        listenOnStart={narrated}
        requireAudio={narrated}
        startAt={index.current}
        onIndex={(i) => { index.current = i; }}
        lens={lens}
        {...(api && narrated ? { loadSpeech: () => produceSpeech(d.directorId, aired.voiceId) ?? Promise.resolve(undefined), mediaUrl: api.media } : { mediaUrl: (u: string) => api?.media(u) ?? u })}
        hold={!!sheet || publishing}
        actions={<>
          <button className="studio-btn" onClick={() => setSheet('remix')} data-testid="remix">
            <span aria-hidden="true">◐</span>{pick({ en: 'Remix', hi: 'रीमिक्स', zh: '重混' }, l)}
          </button>
          <div className="pov" role="radiogroup" aria-label={pick({ en: 'Perspective', hi: 'नज़रिया', zh: '视角' }, l)}>
            <span className="pov__label">POV</span>
            {teams.map((team) => (
              <button key={team} role="radio" aria-checked={pov === team} className={pov === team ? 'is-on' : ''} style={{ '--t': teamAccent(team) } as CSSProperties} data-testid={`pov-${team}`}
                onClick={() => { if (pov !== team) void air({ ...aired, focus: { kind: 'team', team } as Focus }, gate, team); }}>
                <TeamLogo tricode={team} size={16} />{team}
              </button>
            ))}
          </div>
        </>}
        credit={<>
          {directed && <b>{pick({ en: 'Directed by AI', hi: 'AI द्वारा निर्देशित', zh: 'AI执导' }, l)}</b>}
          <span>{pick(STYLE_COPY[effective.style].name, l)}</span>
          <span>{pick(ENERGY_COPY[effective.energy], l)}</span>
          <span data-testid="credit-voice">{voiceName}</span>
          {pov && <span>{sc.fanOf(directorTeamName(pov, l))}</span>}
        </>}
        banner={banner ? <div className="pov-flash" key={banner.at} style={{ '--t': teamAccent(banner.team) } as CSSProperties} data-testid="pov-banner"><TeamLogo tricode={banner.team} size={44} /><span><small>{pick({ en: 'Now watching as', hi: 'अब इस नज़रिए से', zh: '现在的视角' }, l)}</small><b>{sc.fanOf(directorTeamName(banner.team, l))}</b></span></div> : undefined}
        onClose={close}
        onFinished={() => {
          if (!api || !narrated || seenSent.current.has(d.directorId)) return;
          seenSent.current.add(d.directorId);
          void api.seen(roomId, fan.fanId, d.asOf.seq).catch(() => undefined);
        }}
        controls={<LangToggle value={l} onChange={switchLang} />}
        end={
          <VerdictCard
            verdict={verdict}
            director={d}
            {...(friend ? { friend } : {})}
            language={l}
            actions={<>
              {!final && <button className="btn btn--primary btn--block" onClick={close} data-testid="to-room">{live ? pick({ en: 'Back to live game', hi: 'लाइव मैच पर वापस जाएँ', zh: '返回实时比赛' }, l) : t.watchFinish}</button>}
              <button className={`btn ${final ? 'btn--primary' : 'btn--ghost'} btn--block`} onClick={() => setPublishing(true)} data-testid="make-story">{t.makeStory}</button>
              {verdict.cta === 'rematch' && <button className="btn btn--ghost btn--block" onClick={() => navigate('/')} data-testid="rematch">{t.rematch}</button>}
              {final && <button className="btn btn--ghost btn--block" onClick={close} data-testid="to-room">{t.backToRoom}</button>}
            </>}
          />
        }
      />
      {publishing && <PublishSheet director={d} fan={fan} verdict={verdict} language={l} onClose={() => setPublishing(false)} credit={`${pick(STYLE_COPY[effective.style].name, l)} · ${voiceName}`} />}
      {sheetEl}
      {debugEnabled() && <DebugInspector director={d} {...(api?.inspector ? { load: inspect } : {})} voice={voiceName} />}
    </main>
  );
}

/** Buffer one chapter's audio (bounded wait: a slow network still starts on the master clock). */
function bufferAudio(url: string, timeoutMs = 2500): Promise<void> {
  return new Promise((resolve) => {
    if (typeof Audio === 'undefined') return resolve();
    const a = new Audio();
    const done = () => { clearTimeout(t); a.oncanplaythrough = a.onloadeddata = a.onerror = null; resolve(); };
    const t = setTimeout(done, timeoutMs);
    a.preload = 'auto';
    a.oncanplaythrough = done;
    a.onloadeddata = done;
    a.onerror = done;
    a.src = url;
    a.load();
  });
}

const lensCache = new Map<string, Lens>();
/** Stable lens object per (followed, familiarity) so the player's memoized scenes don't churn. */
function lensFor(followed: readonly string[], familiarity: Lens['familiarity']): Lens {
  const key = `${followed.join(',')}|${familiarity}`;
  let v = lensCache.get(key);
  if (!v) lensCache.set(key, (v = { followed: [...followed], ...(familiarity ? { familiarity } : {}) }));
  return v;
}

function StateCard({ title, action, onAction }: { title: string; action: string; onAction: () => void }) {
  return (
    <div className="cu__card" role="status">
      <span className="cu__mark" aria-hidden="true" />
      <p>{title}</p>
      <button className="btn btn--primary" onClick={onAction}>{action}</button>
    </div>
  );
}

/**
 * Broadcast "going to air": one transition whose stages are the real request states
 * (generation.ts). No percentages; the elapsed clock is honest wall time.
 */
function GoingToAir({ draft, gate, game, stage, loading, onCancel }: { draft: ShowDraft; gate: VoiceGate; game?: SheetGame; stage: GenerationStage; loading: string; onCancel?: () => void }) {
  const l = draft.language;
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => setSeconds(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  const voice = selectedVoice(gate, draft.voiceId);
  const status = stageStatuses(stage);
  return (
    <div className="air" role="status" aria-live="polite" data-testid="going-to-air" data-stage={stage}>
      <p className="air__standby"><i className="air__tally" aria-hidden="true" />{pick({ en: 'Going to air', hi: 'ऑन एयर की तैयारी', zh: '即将开播' }, l)}</p>
      {game && <div className="air__match" aria-hidden="true"><TeamLogo tricode={game.away} size={56} /><span>{game.away}<i>@</i>{game.home}</span><TeamLogo tricode={game.home} size={56} /></div>}
      <ol className="air__stages">
        {GENERATION_STAGES.map((s) => (
          <li key={s} className={`air__stage is-${status[s]}`} data-testid={`air-stage-${s}`} data-status={status[s]}>
            <i aria-hidden="true" />{pick(STAGE_COPY[s], l)}
          </li>
        ))}
      </ol>
      <p className="air__slate">{pick(STYLE_COPY[draft.style].name, l)} · {pick(ENERGY_COPY[draft.energy], l)}{voice ? ` · ${voice.name}` : ''}</p>
      <p className="air__clock" data-testid="air-elapsed">
        <span>{String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}</span>
        {seconds >= 12 && stage === 'compose' && <small>{pick({ en: 'The model is still writing, and every line is fact-checked. This can take up to 90 seconds.', hi: 'मॉडल अभी लिख रहा है और हर पंक्ति की तथ्य-जाँच होती है। 90 सेकंड तक लग सकते हैं।', zh: '模型仍在撰写，每一句都要核查事实，最多可能需要90秒。' }, l)}</small>}
      </p>
      {onCancel && <button className="air__cancel" onClick={onCancel} data-testid="air-cancel">{pick({ en: 'Cancel', hi: 'रद्द करें', zh: '取消' }, l)}</button>}
      <p className="sr-only">{loading}</p>
    </div>
  );
}

const SHOW_KEY = 'clutch.director.show';
const FOCUS_KEY = 'clutch.director.focus';
function localShow(): Partial<DirectorShow> | undefined {
  try { return (JSON.parse(store.get(SHOW_KEY) ?? 'null') as Partial<DirectorShow> | null) ?? undefined; } catch { return undefined; }
}
function localFocus(): Focus['kind'] | undefined {
  try {
    const v = store.get(FOCUS_KEY);
    return v === 'calls' || v === 'game' || v === 'analysis' ? v : undefined;
  } catch { return undefined; }
}
function saveLocal(d: ShowDraft) {
  try {
    store.set(SHOW_KEY, JSON.stringify({ style: d.style, energy: d.energy, length: d.length, spoilers: d.spoilers, ...(d.voiceId ? { voiceId: d.voiceId } : {}) }));
    store.set(FOCUS_KEY, d.focus.kind);
  } catch { /* memory only */ }
}
